import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactTooltip from 'react-tooltip';

import { uploadAsset, fetchAssets, deleteAsset } from '../../../actions/targets/assets.action';
import '../../../assets/css/assets-popup.css';
import icons from '../../../assets/img/icons.svg';
import assetsImg from '../../../assets/img/assets-white.png';
import warningImg from '../../../assets/img/warning.png';
import pdfIcon from '../../../assets/img/pdf.png';
import { PDF } from '../../../constant/insights';

const AssetsPopup = (props) => {
  const { active, hideDialog } = props;

  const node = useRef({});
  const fileInputRef = useRef(null);
  const confirmNode = useRef(null);
  const dispatch = useDispatch();
  const uploading = useSelector((state) => state.assets.assetsModalProps.uploading);
  const loading = useSelector((state) => state.assets.assetsModalProps.loading);
  const assets = useSelector((state) => state.assets.assetsModalProps.assets);
  const uploadError = useSelector((state) => state.assets.assetsModalProps.uploadError);

  const [assetToDelete, setAssetToDelete] = useState(null);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const closeDialog = () => {
    document.removeEventListener('click', handleOutsideClick, true);
    if (hideDialog) hideDialog();
  };

  const handleOutsideClick = (e) => {
    if (node?.current?.contains(e.target) || confirmNode?.current?.contains(e.target)) {
      return;
    }
    closeDialog();
  };

  const handleDeleteClick = (asset) => {
    setAssetToDelete(asset);
  };

  const confirmDelete = () => {
    if (assetToDelete) {
      dispatch(deleteAsset(assetToDelete.id));
      setAssetToDelete(null);
    }
  };

  const cancelDelete = () => {
    setAssetToDelete(null);
  };

  useEffect(() => {
    if (active) {
      dispatch(fetchAssets());
    }
  }, [active, dispatch]);

  useEffect(() => {
    if (!uploading && active) {
      dispatch(fetchAssets());
    }
  }, [uploading, active, dispatch]);

  useEffect(() => {
    if (active) {
      document.addEventListener('click', handleOutsideClick, true);
      dispatch(fetchAssets());
    }
    return () => {
      document.removeEventListener('click', handleOutsideClick, true);
    };
  }, [active]);

  const handleFile = (file) => {
    setError('');
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setError('Only PDF files are allowed.');
      return;
    }
    dispatch(uploadAsset({ file, name: file.name, users: [] }));
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  return (
    <>
      <div
        className={`dialog assets-dialog${active ? ' active' : ''}${dragActive ? ' drag-active' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="box">
          <div className="close-dialog" onClick={closeDialog}>
            <svg className="icon nav-icon">
              <use xlinkHref={`${icons}#action-close`} />
            </svg>
          </div>
          {active && (
            <div className={`inner ${active ? 'keyboard' : ''}`} ref={node}>
              <div className="info-block assets">
                <fieldset className="assets-fieldset">
                  <legend>
                    <img src={assetsImg} alt="Assets" className="icon asset-icon" />
                    Assets
                  </legend>
                  {loading ? (
                    <div className="assets-loading">Loading...</div>
                  ) : assets && assets.length > 0 ? (
                    <ul className="assets-list">
                      {assets.map((asset) => (
                        <li key={asset.id} className="asset-item">
                          <div className="asset-thumb">
                            <span className="delete-icon" title="Delete" onClick={() => handleDeleteClick(asset)}>
                              <svg className="icon nav-icon">
                                <use xlinkHref={`${icons}#action-close`} />
                              </svg>
                            </span>
                            <a href={asset.file_url} target="_blank" rel="noopener noreferrer" className="asset-link">
                              <img src={pdfIcon} alt="PDF" className="pdf-icon" />
                            </a>
                          </div>
                          <div className="asset-title-wrapper">
                            <span
                              className="asset-title"
                              data-tip={asset.name}
                              data-for={`asset-title-tooltip-${asset.id}`}
                            >
                              {asset.name}
                            </span>
                            <ReactTooltip
                              id={`asset-title-tooltip-${asset.id}`}
                              place="top"
                              effect="solid"
                              multiline={true}
                              className="asset-tooltip"
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No assets available.</p>
                  )}
                </fieldset>
                <div className="buttons-container">
                  {error && (
                    <div className="error-message" style={{ color: 'red', marginBottom: 8 }}>
                      {error}
                    </div>
                  )}
                  {uploadError && (
                    <div className="error-message" style={{ color: 'red', marginBottom: 8 }}>
                      {typeof uploadError === 'string'
                        ? uploadError
                        : uploadError?.response?.data?.message || uploadError?.message || 'Upload failed.'}
                    </div>
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => handleFile(e.target.files[0])}
                    accept="application/pdf"
                  />
                  <button className="upload-button" onClick={triggerFileInput} disabled={uploading}>
                    {uploading ? 'Uploading...' : 'Upload '}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {assetToDelete && (
        <div className="dialog confirm-dialog active" ref={confirmNode}>
          <div className="box box-confirm">
            <div className="close-dialog" onClick={() => setAssetToDelete(null)}>
              <svg className="icon nav-icon">
                <use xlinkHref={`${icons}#action-close`} />
              </svg>
            </div>
            <div className="info-block">
              <fieldset>
                <legend>
                  <img src={warningImg} alt="Assets" className="icon warning-icon" />
                  Warning
                </legend>
                <div className="confirm-content">
                  <p className="confirm-text">
                    You are about to delete {assetToDelete.name} Are you sure you want to perform this action?
                  </p>
                  <div className="confirm-buttons-container">
                    <button className="action danger" onClick={cancelDelete}>
                      Cancel
                    </button>
                    <button className="action" onClick={confirmDelete}>
                      Delete
                    </button>
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssetsPopup;
